/** Pure recipe and IPC contract shared by the GLB atelier and the creature save. */
import type { EquipmentWeaponId as WeaponId } from './equipment';
import {ATELIER_MODEL_IDS} from './atelier-species';
export const ATELIER_COAT_PATTERNS = ['source','solid','tuxedo','tabby','calico','rosettes','marbled','points','saddle','speckled'] as const;
export type AtelierCoatPattern = typeof ATELIER_COAT_PATTERNS[number];
/** Independent pigments. Missing channels inherit the selected palette. */
export interface AtelierColors {
  fur?: string; markings?: string; muzzle?: string;
  fabric?: string; secondary?: string; trim?: string; leather?: string; metal?: string;
}
export const ATELIER_COLOR_CHANNELS = ['fur','markings','muzzle','fabric','secondary','trim','leather','metal'] as const;
export const ATELIER_BOOTS = ['worn','traveller','iron','quiet'] as const;
export const ATELIER_CHARMS = ['reliquary','order-seal','thorn-knot'] as const;
export type AtelierBoots = typeof ATELIER_BOOTS[number];
export type AtelierCharm = typeof ATELIER_CHARMS[number];
export interface Morphs {
  head: number;   // 0.8 .. 1.35
  belly: number;  // 0.8 .. 1.45
  legs: number;   // 0.75 .. 1.3
  arms: number;   // 0.8 .. 1.25
  tail: number;   // 0.6 .. 1.5
  ears: number;   // 0.6 .. 1.6
}

export interface FaceShape {
  eyeSpacing: number; eyeY: number; eyeSize: number; eyeAspect: number; eyeTilt: number;
  irisSize: number; pupilSize: number; pupilSlit: number; irisColor: [number, number, number];
  lidHeavy: number; bags: number; derp: number;
  browY: number; browAngle: number; browThickness: number; browCurve: number; browLength: number;
  noseY: number; noseSize: number; noseColor: [number, number, number];
  mouthY: number; mouthWidth: number; mouthSmile: number; fangs: number;
  blush: number; whiskerDots: number;
  eraseRadius: number; eraseY: number;
  /** Keep the source sculpted nose and muzzle; animate eyes, brows and a local inked mouth. */
  naturalMuzzle: boolean;
}

export interface AtelierAppearance {
  version: 1;
  name: string;
  model: string;
  head: string;
  coat: string;
  outfit: string;
  morphs: Morphs;
  face: FaceShape;
  emotion: string;
  /** Optional for version-one saves created before fabric dyes were available. */
  dye?: AtelierDye;
  coatPattern?: AtelierCoatPattern;
  coatSeed?: number;
  coatScale?: number;
  colors?: AtelierColors;
  /** Atelier prop; gameplay derives the weapon from the equipped build. */
  weapon?: WeaponId;
  boots?: AtelierBoots;
  charm?: AtelierCharm;
}
export const ATELIER_DYES = ['original','crimson','lapis','forest','ochre','plum','ash'] as const;
export type AtelierDye = typeof ATELIER_DYES[number];
export const ATELIER_DRAFT = 'marginalia:atelier:draft:v1';
export const ATELIER_HERO = 'marginalia:atelier:hero:v1';
/** One explicit same-tab handoff from the standalone atelier to the game. */
export const ATELIER_TRANSFER = 'marginalia:atelier:transfer:v1';
/** Original in-game recipe survives a language reload inside the fitting iframe. */
export const ATELIER_BASELINE = 'marginalia:atelier:baseline:v1';

/** Imported messages and local drafts are data, never arbitrary renderer parameters. */
export function readAppearance(value: unknown): AtelierAppearance | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Partial<AtelierAppearance>;
  if(v.dye!==undefined && !ATELIER_DYES.includes(v.dye))return null;
  if(v.coatPattern!==undefined&&!ATELIER_COAT_PATTERNS.includes(v.coatPattern))return null;
  if(v.coatSeed!==undefined&&(!Number.isInteger(v.coatSeed)||v.coatSeed<0||v.coatSeed>65535))return null;
  if(v.coatScale!==undefined&&(!Number.isFinite(v.coatScale)||v.coatScale<.6||v.coatScale>1.6))return null;
  if(v.weapon!==undefined&&!['sword','hammer','staff','daggers'].includes(v.weapon))return null;
  if(v.boots!==undefined&&!ATELIER_BOOTS.includes(v.boots))return null;
  if(v.charm!==undefined&&!ATELIER_CHARMS.includes(v.charm))return null;
  let colors:AtelierColors|undefined;
  if(v.colors!==undefined){
    if(!v.colors||typeof v.colors!=='object'||Array.isArray(v.colors))return null;
    colors={};
    for(const key of ATELIER_COLOR_CHANNELS){
      const color=v.colors[key];if(color===undefined)continue;
      if(typeof color!=='string'||!/^#[0-9a-fA-F]{6}$/.test(color))return null;
      colors[key]=color.toLowerCase();
    }
  }
  const models:readonly string[] = ATELIER_MODEL_IDS;
  if (v.version !== 1 || !models.includes(v.model ?? '') || !['own', ...models].includes(v.head ?? '') ||
    !['orig','ginger','grey','black','ermine','ink','choc','silver','copper','cream','sable','rose','sage'].includes(v.coat ?? '') ||
    !['none','crusader','monk','king','bishop','wanderer','jester'].includes(v.outfit ?? '') ||
    typeof v.name !== 'string' || v.name.length > 16 || !v.morphs || !v.face ||
    !['calm','smug','happy','focused','determined','curious','angry','sad','surprised','scared','sleepy','love','disgust'].includes(v.emotion ?? '')) return null;
  const limits = {head:[.8,1.35],belly:[.8,1.45],legs:[.75,1.3],arms:[.8,1.25],tail:[.6,1.5],ears:[.6,1.6]};
  for (const [key, [lo, hi]] of Object.entries(limits)) {
    const n = v.morphs[key as keyof Morphs]; if (!Number.isFinite(n) || n < lo || n > hi) return null;
  }
  const scalarKeys = ['eyeSpacing','eyeY','eyeSize','eyeAspect','eyeTilt','irisSize','pupilSize','pupilSlit','lidHeavy','bags','derp','browY','browAngle','browThickness','browCurve','browLength','noseY','noseSize','mouthY','mouthWidth','mouthSmile','fangs','blush','whiskerDots','eraseRadius','eraseY'] as const;
  const face = {} as FaceShape;
  for (const key of scalarKeys) {
    const entry = v.face[key];
    if (typeof entry !== 'number' || !Number.isFinite(entry) || Math.abs(entry) > 4) return null;
    face[key] = entry;
  }
  for (const key of ['irisColor','noseColor'] as const) {
    const color = v.face[key];
    if (!Array.isArray(color) || color.length !== 3 || color.some(n => typeof n !== 'number' || !Number.isFinite(n) || n < 0 || n > 1)) return null;
    face[key] = [...color];
  }
  if (typeof v.face.naturalMuzzle !== 'boolean') return null;
  face.naturalMuzzle = v.face.naturalMuzzle;
  return {version:1,name:v.name.trim(),model:v.model!,head:v.head!,coat:v.coat!,outfit:v.outfit!,emotion:v.emotion!,...(v.dye===undefined?{}:{dye:v.dye}),...(v.coatPattern===undefined?{}:{coatPattern:v.coatPattern}),...(v.coatSeed===undefined?{}:{coatSeed:v.coatSeed}),...(v.coatScale===undefined?{}:{coatScale:v.coatScale}),...(v.weapon===undefined?{}:{weapon:v.weapon}),...(v.boots===undefined?{}:{boots:v.boots}),...(v.charm===undefined?{}:{charm:v.charm}),...(colors===undefined?{}:{colors}),morphs:{head:v.morphs.head,belly:v.morphs.belly,legs:v.morphs.legs,arms:v.morphs.arms,tail:v.morphs.tail,ears:v.morphs.ears},face};
}

export function validateHeroName(raw:string):string|null {
 const s=raw.normalize('NFC').trim();
 return s.length>=2&&s.length<=16&&/^[А-Яа-яЁёA-Za-z]+([ -][А-Яа-яЁёA-Za-z]+)?$/.test(s)?s:null;
}
